import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

// Services
import { MemberService } from "services/member.service";
import { ContributionService } from "services/contribution.service";
import { OfferingService } from "services/offering.service";
import { TitheService } from "services/tithe.service";
import { FundService } from "services/fund.service";
import { ExpenseService } from "services/expense.service";
import { AttendanceService } from "services/attendance.service";
import { EventService } from "services/event.service";
import { ContributionTypeService } from "services/contribution-type.service";
import { DepartmentService } from "services/department.service";
import { IUser, IEventResponse } from "models/church.types";
import { hasAnyAuthority, ROLES } from "services/security.utils";

// Components
import CardStats from "components/Cards/CardStats";
import CardLineChart from "components/Cards/CardLineChart";
import CardBarChart from "components/Cards/CardBarChart";
import CardDoughnutChart from "components/Cards/CardDoughnutChart";
import UserService from "services/user.service";

// Interfaces
interface SelectOption {
  value: string | number;
  label: string;
}

interface TrendState {
  percent: string;
  color: string;
  arrow: "up" | "down" | "none";
}

export default function Dashboard() {
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // --- ÉTATS ---
  const [stats, setStats] = useState({
    totalMembers: 0,
    monthlyContributions: 0,
    monthlyOfferings: 0,
    monthlyTithes: 0,
    monthlyExpenses: 0,
    totalEvents: 0,
    lastAttendanceCount: 0,
    lastEventName: "Aucun événement",
    lastId: null as number | null,
    participationRate: 0,
  });

  // État pour mémoriser le nombre total de membres global
  const [totalMembersGlobal, setTotalMembersGlobal] = useState(0);

  const [doughnutData, setDoughnutData] = useState<any[]>([]);
  const [doughnutLoading, setDoughnutLoading] = useState(false);
  const [lineChartData, setLineChartData] = useState<number[]>([]);
  const [lineChartLabels, setLineChartLabels] = useState<string[]>([]);
  const [lineLoading, setLineLoading] = useState(false);
  const [barData, setBarData] = useState<{
    labels: string[];
    values: number[];
  }>({ labels: [], values: [] });
  const [barLoading, setBarLoading] = useState(false);

  const [eventOptions, setEventOptions] = useState<SelectOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<SelectOption[]>([]);
  const [deptOptions, setDeptOptions] = useState<SelectOption[]>([]);

  const [selectedEventId, setSelectedEventId] = useState<string | number>("");
  const [selectedTypeId, setSelectedTypeId] = useState<string | number>("");
  const [selectedDeptId, setSelectedDeptId] = useState<string | number>("");

  const [contributionTrend, setContributionTrend] = useState<TrendState>({
    percent: "",
    color: "text-slate-400",
    arrow: "none",
  });

  const [loading, setLoading] = useState(true);

  // --- STYLES REACT-SELECT ---
  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      minHeight: "30px",
      cursor: "pointer",
      width: "100%",
    }),
    valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
    input: (base: any) => ({ ...base, color: "white", fontSize: "11px" }),
    placeholder: (base: any) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "11px",
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
      fontSize: "11px",
      fontWeight: "600",
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      width: "max-content",
      minWidth: "100%",
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#334155" : "transparent",
      color: "white",
      padding: "8px 12px",
      fontSize: "11px",
      cursor: "pointer",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#64748b",
      padding: "4px",
    }),
  };

  // --- LOGIQUE TEMPORELLE ---
  const getPeriods = () => {
    const now = new Date();
    return {
      startCurrent: new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      ).toISOString(),
      startLast: new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        1
      ).toISOString(),
      endLast: new Date(
        now.getFullYear(),
        now.getMonth(),
        0,
        23,
        59,
        59
      ).toISOString(),
    };
  };

  // Modification : Récupération du montant total sans restriction de mois en cours
  const calculateTotalContributions = async (typeId?: any) => {
    try {
      // Appel sans dates de début/fin pour obtenir le cumul total global ou par type
      const totalAmount = await ContributionService.getSummary(
        undefined,
        undefined,
        typeId
      );

      setContributionTrend({
        percent: "",
        color: "text-slate-400",
        arrow: "none",
      });

      return totalAmount || 0;
    } catch {
      return 0;
    }
  };

  // ✅ LINE CHART : Cumul (Cotisations + Offrandes + Dîmes) sur 6 mois
  const fetchLineChartData = async () => {
    setLineLoading(true);
    const months = [
      "Jan",
      "Fév",
      "Mar",
      "Avr",
      "Mai",
      "Juin",
      "Juil",
      "Août",
      "Sep",
      "Oct",
      "Nov",
      "Déc",
    ];
    const now = new Date();
    const tempLabels: string[] = [];
    const tempData: number[] = [];
    try {
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const endD = new Date(
          now.getFullYear(),
          now.getMonth() - i + 1,
          0,
          23,
          59,
          59
        );
        tempLabels.push(months[d.getMonth()]);

        const [contributionsAmount, offeringsAmount, tithesAmount] =
          await Promise.all([
            ContributionService.getSummary(
              d.toISOString(),
              endD.toISOString()
            ).catch(() => 0),
            OfferingService.getSummary(
              d.toISOString(),
              endD.toISOString()
            ).catch(() => 0),
            TitheService.getSummary(
              d.toISOString(),
              endD.toISOString()
            ).catch(() => 0),
          ]);

        const totalRevenue =
          (contributionsAmount || 0) +
          (offeringsAmount || 0) +
          (tithesAmount || 0);

        tempData.push(totalRevenue);
      }
      setLineChartLabels(tempLabels);
      setLineChartData(tempData);
    } finally {
      setLineLoading(false);
    }
  };

  const loadBarStats = async (events: IEventResponse[]) => {
    setBarLoading(true);
    try {
      const lastEvents = events.slice(0, 10);
      const results = await Promise.all(
        lastEvents.map(async (ev) => {
          const res = await AttendanceService.getByEvent(ev.id, 0, 1);
          return { title: ev.title, count: res.totalCount || 0 };
        })
      );
      setBarData({
        labels: results.map((r) => r.title),
        values: results.map((r) => r.count),
      });
    } finally {
      setBarLoading(false);
    }
  };

  // ✅ DOUGHNUT CHART : Filtrage des types par statut officiel du fund
  const loadDoughnutStats = async (types: any[]) => {
    setDoughnutLoading(true);
    try {
      const { startCurrent } = getPeriods();

      const fundsResponse = await FundService.getAll(0, 100).catch(() => ({
        data: [],
      }));
      const allFunds = fundsResponse.data || [];

      const officialFundIds = new Set(
        allFunds.filter((f: any) => f.official === true).map((f: any) => f.id)
      );

      const officialTypes = types.filter((type) =>
        officialFundIds.has(type.fundId)
      );

      const contributionsByType = await Promise.all(
        officialTypes.map(async (type) => {
          const amount = await ContributionService.getSummary(
            startCurrent,
            undefined,
            type.id
          ).catch(() => 0);
          return { label: type.name, amount: amount || 0 };
        })
      );

      const [offeringsTotal, tithesTotal] = await Promise.all([
        OfferingService.getSummary(startCurrent).catch(() => 0),
        TitheService.getSummary(startCurrent).catch(() => 0),
      ]);

      const allRevenueSources = [
        ...contributionsByType,
        { label: "Dîmes", amount: tithesTotal || 0 },
        { label: "Offrandes", amount: offeringsTotal || 0 },
      ];

      setDoughnutData(allRevenueSources.filter((r: any) => r.amount > 0));
    } finally {
      setDoughnutLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const { startCurrent } = getPeriods();
      const [
        membersRes,
        eventsRes,
        totalContributionsSum,
        latestAttendanceRes,
        typesRes,
        offeringsSum,
        tithesSum,
        expensesSum,
        departmentsRes,
      ] = await Promise.all([
        MemberService.getAll(0, 1).catch(() => ({ totalCount: 0 })),
        EventService.getAll(0, 100)
          .then((res) => res.data)
          .catch(() => []),
        calculateTotalContributions(),
        AttendanceService.search("", 0, 500).catch(() => ({
          data: [],
          totalCount: 0,
        })),
        ContributionTypeService.getAll(0, 100).catch(() => ({ data: [] })),
        OfferingService.getSummary(startCurrent).catch(() => 0),
        TitheService.getSummary(startCurrent).catch(() => 0),
        ExpenseService.getSummary(startCurrent).catch(() => 0),
        DepartmentService.getAllSimple().catch(() => []),
      ]);

      const cleanTypes = Array.isArray(typesRes)
        ? typesRes
        : (typesRes as any)?.data || [];
      const cleanEvents: IEventResponse[] = Array.isArray(eventsRes)
        ? eventsRes
        : [];

      setEventOptions(
        cleanEvents.map((e) => ({ value: e.id, label: e.title }))
      );
      setTypeOptions(cleanTypes.map((t) => ({ value: t.id, label: t.name })));
      setDeptOptions(
        departmentsRes.map((d) => ({ value: d.id, label: d.name }))
      );

      loadDoughnutStats(cleanTypes);
      loadBarStats(cleanEvents);
      fetchLineChartData();

      const totalM = membersRes?.totalCount || 0;
      let lastAttendanceInfo = {
        count: 0,
        name: "Dernier programme",
        id: null as number | null,
      };

      setTotalMembersGlobal(totalM);

      if (latestAttendanceRes?.data?.length > 0) {
        const lastId = latestAttendanceRes.data[0].eventId;
        const count = latestAttendanceRes.data.filter(
          (a: any) => a.eventId === lastId
        ).length;
        const eventObj = cleanEvents.find((e: any) => e.id === lastId);
        lastAttendanceInfo = {
          count,
          name: eventObj?.title || "Dernier programme",
          id: lastId,
        };
      }

      setStats({
        totalMembers: totalM,
        totalEvents: cleanEvents.length,
        monthlyContributions: totalContributionsSum || 0,
        monthlyOfferings: offeringsSum || 0,
        monthlyTithes: tithesSum || 0,
        monthlyExpenses: expensesSum || 0,
        lastAttendanceCount: lastAttendanceInfo.count,
        lastEventName: lastAttendanceInfo.name,
        lastId: lastAttendanceInfo.id,
        participationRate:
          totalM > 0
            ? Math.round((lastAttendanceInfo.count / totalM) * 100)
            : 0,
      });
    } catch (err) {
      console.error("Erreur dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    UserService.getCurrentAccount()
      .then((res) => setCurrentUser(res.data))
      .catch(() => console.error("Erreur chargement profil"))
      .finally(() => setLoadingUser(false));
    fetchDashboardData();
  }, []);

  const isFinanceAuthorized = hasAnyAuthority(currentUser?.authorities, [
    ROLES.ADMIN,
    ROLES.PASTEUR,
    ROLES.TRESORERIE,
  ]);

  if (loadingUser)
    return (
      <div className="text-white p-4">Chargement du tableau de bord...</div>
    );

  const getPercentageString = () => {
    if (!totalMembersGlobal || totalMembersGlobal === 0) return "--";
    if (!selectedDeptId) return "100%";
    const currentCount = stats.totalMembers || 0;
    const ratio = (currentCount / totalMembersGlobal) * 100;
    return `${ratio.toFixed(1)}%`;
  };

  // --- HANDLERS ---
  const handleDeptChange = async (selected: any) => {
    const val = selected ? selected.value : "";
    setSelectedDeptId(val);

    if (!val) {
      fetchDashboardData();
    } else {
      try {
        const res = await MemberService.getByDepartment(val, 0, 1);
        setStats((prev) => ({
          ...prev,
          totalMembers: res.totalCount,
          participationRate:
            totalMembersGlobal > 0
              ? Math.round(
                  (prev.lastAttendanceCount / totalMembersGlobal) * 100
                )
              : 0,
        }));
      } catch (err) {
        console.error("Erreur filtre dept:", err);
      }
    }
  };

  const handleEventChange = async (selected: any) => {
    const val = selected ? selected.value : "";
    setSelectedEventId(val);

    if (!val) {
      fetchDashboardData();
      return;
    }

    try {
      const res = await AttendanceService.getByEvent(Number(val), 0, 500);
      setStats((prev) => ({
        ...prev,
        lastAttendanceCount: res.totalCount || 0,
        lastEventName: selected.label,
        participationRate:
          totalMembersGlobal > 0
            ? Math.round((res.totalCount / totalMembersGlobal) * 100)
            : 0,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleTypeChange = async (selected: any) => {
    const val = selected ? selected.value : "";
    setSelectedTypeId(val);
    const amount = await calculateTotalContributions(val || undefined);
    setStats((prev) => ({ ...prev, monthlyContributions: amount || 0 }));
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4 mt-8 relative z-20">
        {/* COL 1: MEMBRES */}
        <div className="w-full transition-transform hover:scale-105">
          <div className="bg-[#1E293B] border border-slate-700 rounded-full px-3 shadow-md mb-2 h-[42px] flex items-center hover:border-blue-500 transition-colors">
            <i className="fas fa-search text-blue-500 mr-2 text-[10px]"></i>
            <div className="flex-1">
              <Select
                options={deptOptions}
                styles={customSelectStyles}
                placeholder="Filtrer par département..."
                isClearable
                onChange={handleDeptChange}
                value={deptOptions.find((o) => o.value === selectedDeptId)}
                menuPortalTarget={document.body}
              />
            </div>
          </div>
          <Link
            to={
              selectedDeptId
                ? `/admin/members?deptId=${selectedDeptId}`
                : "/admin/members"
            }
            className="block"
          >
            <CardStats
              statSubtitle="Membres"
              statTitle={stats.totalMembers.toString()}
              statArrow={selectedDeptId ? "none" : "up"}
              statPercent={getPercentageString()}
              statPercentColor={
                selectedDeptId ? "text-slate-400" : "text-emerald-500"
              }
              statDescripiron={
                selectedDeptId ? "Effectif filtré" : "Effectif total"
              }
              statIconName="fas fa-users"
              statIconColor="bg-blue-500"
            />
          </Link>
        </div>

        {/* COL 2: PARTICIPATION */}
        <div className="w-full transition-transform hover:scale-105">
          <div className="bg-[#1E293B] border border-slate-700 rounded-full px-3 shadow-md mb-2 h-[42px] flex items-center hover:border-emerald-500 transition-colors">
            <i className="fas fa-search text-emerald-500 mr-2 text-[10px]"></i>
            <div className="flex-1">
              <Select
                options={eventOptions}
                styles={customSelectStyles}
                placeholder="Filtrer par évenement..."
                isClearable
                onChange={handleEventChange}
                value={eventOptions.find((o) => o.value === selectedEventId)}
                menuPortalTarget={document.body}
              />
            </div>
          </div>
          <Link
            to={
              selectedEventId || stats.lastId
                ? `/admin/attendances?eventId=${selectedEventId || stats.lastId}`
                : "/admin/attendances"
            }
            className="block"
          >
            <CardStats
              statSubtitle="Participation"
              statTitle={`${stats.lastAttendanceCount} Présents`}
              statArrow={stats.participationRate >= 50 ? "up" : "down"}
              statPercent={`${stats.participationRate}%`}
              statPercentColor={
                stats.participationRate >= 50
                  ? "text-emerald-500"
                  : "text-red-500"
              }
              statDescripiron={stats.lastEventName}
              statIconName="fas fa-user-check"
              statIconColor="bg-emerald-500"
            />
          </Link>
        </div>

        {isFinanceAuthorized && (
          <>
            {/* COL 3: COTISATIONS */}
            <div className="w-full transition-transform hover:scale-105">
              <div className="bg-[#1E293B] border border-slate-700 rounded-full px-3 shadow-md mb-2 h-[42px] flex items-center hover:border-[#D4AF37] transition-colors">
                <i className="fas fa-search text-[#D4AF37] mr-2 text-[10px]"></i>
                <div className="flex-1">
                  <Select
                    options={typeOptions}
                    styles={customSelectStyles}
                    placeholder="Filtrer par type de contribution..."
                    isClearable
                    onChange={handleTypeChange}
                    value={typeOptions.find((o) => o.value === selectedTypeId)}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
              <Link
                to={
                  selectedTypeId
                    ? `/admin/contributions?typeId=${selectedTypeId}`
                    : "/admin/contributions"
                }
                className="block"
              >
                <CardStats
                  statSubtitle="Contributions"
                  statTitle={`${stats.monthlyContributions.toLocaleString()} FCFA`}
                  statArrow="none"
                  statPercent=""
                  statPercentColor="text-slate-400"
                  statDescripiron={
                    selectedTypeId
                      ? typeOptions.find((o) => o.value === selectedTypeId)
                          ?.label || ""
                      : "Cumul total global"
                  }
                  statIconName="fas fa-hand-holding-usd"
                  statIconColor="bg-[#D4AF37]"
                />
              </Link>
            </div>

            {/* AUTRES STATS FINANCIÈRES (OFFRANDES ET DÎMES CÔTE À CÔTE) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full xl:col-span-2">
              {/* CARTE OFFRANDES */}
              <div className="w-full transition-transform hover:scale-105">
                <Link to="/admin/offerings" className="block">
                  <CardStats
                    statSubtitle="Offrandes"
                    statTitle={`${stats.monthlyOfferings.toLocaleString()} FCFA`}
                    statDescripiron="Cumul du mois"
                    statIconName="fas fa-gifts"
                    statIconColor="bg-purple-500"
                  />
                </Link>
              </div>

              {/* CARTE DÎMES */}
              <div className="w-full transition-transform hover:scale-105">
                <Link to="/admin/tithes" className="block">
                  <CardStats
                    statSubtitle="Dîmes"
                    statTitle={`${stats.monthlyTithes.toLocaleString()} FCFA`}
                    statDescripiron="Cumul du mois"
                    statIconName="fas fa-hand-holding-usd"
                    statIconColor="bg-emerald-500"
                  />
                </Link>
              </div>
            </div>

            <div className="w-full transition-transform hover:scale-105">
              <Link to="/admin/expenses" className="block">
                <CardStats
                  statSubtitle="Dépenses"
                  statTitle={`${stats.monthlyExpenses.toLocaleString()} FCFA`}
                  statDescripiron="Sorties du mois"
                  statIconName="fas fa-wallet"
                  statIconColor="bg-red-500"
                />
              </Link>
            </div>
          </>
        )}

        <div className="w-full transition-transform hover:scale-105">
          <Link to="/admin/events" className="block">
            <CardStats
              statSubtitle="Programmes"
              statTitle={stats.totalEvents.toString()}
              statArrow="none"
              statDescripiron="Total activités"
              statIconName="fas fa-calendar-alt"
              statIconColor="bg-orange-500"
            />
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap">
        <div className="w-full xl:w-8/12">
          <div className="flex flex-col">
            <CardBarChart
              title="Fréquentation par Programme"
              labels={barData.labels}
              data={barData.values}
              loading={barLoading}
            />
            {isFinanceAuthorized && (
              <CardLineChart
                title="Performance Financière Globale"
                subtitle="Cotisations + Offrandes + Dîmes (6 derniers mois)"
                data={lineChartData}
                labels={lineChartLabels}
                loading={lineLoading}
              />
            )}
          </div>
        </div>
        {isFinanceAuthorized && (
          <div className="w-full xl:w-4/12 px-4">
            <CardDoughnutChart
              title="Sources de Revenus"
              data={doughnutData}
              loading={doughnutLoading}
            />
          </div>
        )}
      </div>
    </>
  );
}